import { useState } from 'react';
import { useAvatarPresign, useUpdateProfile } from '../lib/api';

/**
 * Click-to-upload avatar. Uses /me/avatar to get a presigned PUT URL,
 * uploads JPEG directly to S3, then PATCHes /me with the new key so the
 * profile picks it up on next render.
 */
export function AvatarUpload({ initials, currentKey }: { initials: string; currentKey?: string | null }) {
  const presign = useAvatarPresign();
  const update  = useUpdateProfile();
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) return alert('Please choose an image.');
    setBusy(true);
    try {
      const local = URL.createObjectURL(file);
      setPreviewUrl(local);
      const p = await presign.mutateAsync();
      await fetch(p.url, { method: 'PUT', body: file, headers: { 'Content-Type': 'image/jpeg' } });
      await update.mutateAsync({ avatarS3Key: p.key } as any);
    } finally {
      setBusy(false);
    }
  };

  return (
    <label
      className="relative inline-block cursor-pointer group rounded-full focus-within:ring-2 focus-within:ring-squishy-teal focus-within:ring-offset-2"
      aria-label="Change profile photo"
    >
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-squishy-teal to-squishy-tealDark text-white flex items-center justify-center text-2xl font-display font-semibold overflow-hidden shadow-card">
        {previewUrl ? (
          <img src={previewUrl} alt="" className="w-full h-full object-cover" />
        ) : currentKey ? (
          <img src={`https://squishy-uploads-${import.meta.env.VITE_AWS_ACCOUNT}.s3.amazonaws.com/${currentKey}`} alt="" className="w-full h-full object-cover" />
        ) : (
          initials
        )}
      </div>
      <input
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
      {busy && (
        <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center" aria-hidden="true">
          <span className="block w-5 h-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
        </div>
      )}
      <span
        className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-white border border-slate-200 shadow-card flex items-center justify-center text-slate-500 group-hover:text-squishy-tealDark group-active:scale-95 transition"
        aria-hidden="true"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M1 8a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 018.07 3h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0016.07 6H17a2 2 0 012 2v7a2 2 0 01-2 2H3a2 2 0 01-2-2V8zm13.5 3a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM10 14a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      </span>
    </label>
  );
}
